<template>
  <div>
  <nav
    class="relative flex items-center justify-between bg-dark-nav shadow-[0_1px_4px_rgba(0,0,0,0.18)]"
    :class="[
      'px-3 py-2',
      'sm:px-7 sm:py-2',
    ]"
  >

    <!-- Mobile nav (< 640px) -->
    <div class="flex sm:hidden items-center justify-between w-full">
      <!-- Left: hamburger -->
      <button
        @click="menuOpen = !menuOpen"
        class="p-2 min-h-[44px] flex items-center justify-center text-white"
        aria-label="Toggle menu"
      >
        <Menu v-if="!menuOpen" :size="24" />
        <X v-else :size="24" />
      </button>

      <!-- Center: logo -->
      <router-link to="/" class="absolute left-1/2 -translate-x-1/2">
        <img src="/logo-big.svg" alt="Gettourly" class="h-12" />
      </router-link>

      <!-- Right: Join button or avatar -->
      <router-link
        v-if="!authStore.isAuthenticated"
        to="/login"
        class="px-3 py-1 text-[13px] font-medium text-white/90 bg-transparent border border-white/45 rounded-md hover:bg-white/10 transition-colors"
      >Join</router-link>
      <div v-else class="flex items-center gap-2">
        <router-link
          :to="{ name: 'pricing' }"
          class="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/10 hover:bg-white/15 text-white text-xs font-medium transition-colors"
          :title="`${authStore.creditsBalance} credits — add more`"
        >
          <Coins :size="12" />
          {{ authStore.creditsBalance }}
        </router-link>
        <button
          @click="menuOpen = !menuOpen"
          class="w-9 h-9 rounded-full overflow-hidden flex items-center justify-center text-white font-semibold text-sm select-none bg-primary"
          :title="authStore.user?.email"
        >
          <img v-if="authStore.user?.avatar_url" :src="authStore.user.avatar_url" alt="Avatar" class="w-full h-full object-cover" />
          <span v-else>{{ avatarLetter }}</span>
        </button>
      </div>
    </div>

    <!-- Desktop nav (>= 640px) -->
    <div class="hidden sm:flex items-center justify-between w-full">
      <div class="flex items-center gap-3">
        <router-link to="/">
          <img src="/logo-big.svg" alt="Gettourly" class="h-[52px]" />
        </router-link>
        <div class="w-px h-5 bg-white/25 mx-1"></div>
        <router-link
          to="/pricing"
          class="px-3.5 py-[5px] text-[13px] font-medium text-white border border-white/25 rounded-md transition-colors"
          :class="route.path === '/pricing' ? 'bg-white/15' : 'hover:bg-white/10'"
        >Pricing</router-link>
      </div>

      <div class="flex items-center gap-2.5">
        <template v-if="authStore.isAuthenticated">
          <router-link
            to="/library"
            class="px-3.5 py-[5px] text-[13px] font-medium text-white border border-white/25 rounded-md transition-colors inline-flex items-center gap-1.5"
            :class="isLibraryActive ? 'bg-white/15' : 'hover:bg-white/10'"
          >
            <LayoutGrid :size="14" />
            Videos
          </router-link>
          <!-- The credits pill is the way to more credits: /pricing carries
               the contact CTA for a signed-in user. -->
          <router-link
            :to="{ name: 'pricing' }"
            class="inline-flex items-center gap-1 px-3 py-[5px] rounded-md bg-white/10 hover:bg-white/15 transition-colors text-white text-[13px] font-medium"
            :title="`${authStore.creditsBalance} credits — add more`"
          >
            <Coins :size="14" />
            {{ authStore.creditsBalance }}
          </router-link>
          <div ref="userMenuRef" class="relative">
            <button
              @click="userMenuOpen = !userMenuOpen"
              class="w-[34px] h-[34px] rounded-full overflow-hidden flex items-center justify-center text-white font-semibold text-[13px] select-none bg-primary ring-offset-2 ring-offset-dark-nav transition focus:ring-2 focus:ring-white/40"
              :title="authStore.user?.email"
              aria-haspopup="menu"
              :aria-expanded="userMenuOpen"
            >
              <img v-if="authStore.user?.avatar_url" :src="authStore.user.avatar_url" alt="Avatar" class="w-full h-full object-cover" />
              <span v-else>{{ avatarLetter }}</span>
            </button>
            <div
              v-if="userMenuOpen"
              class="absolute right-0 mt-2 w-52 rounded-[10px] bg-surface shadow-lg border border-border overflow-hidden z-20"
              role="menu"
            >
              <div class="px-3 py-2 text-xs text-muted truncate border-b border-border">
                {{ authStore.user?.email }}
              </div>
              <button
                @click="openAccountSettings(); userMenuOpen = false"
                class="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-text hover:bg-bg"
                role="menuitem"
              >
                <Settings :size="15" />
                Account settings
              </button>
              <button
                @click="handleLogout(); userMenuOpen = false"
                class="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-text hover:bg-bg border-t border-border"
                role="menuitem"
              >
                <LogOut :size="15" />
                Log out
              </button>
            </div>
          </div>
        </template>
        <template v-else>
          <router-link
            to="/login"
            class="px-[18px] py-[5px] text-[13px] font-medium text-white/90 bg-transparent border border-white/45 rounded-md hover:bg-white/10 transition-colors"
          >Join</router-link>
        </template>
      </div>
    </div>

  </nav>

  <!-- Mobile dropdown menu -->
  <div v-if="menuOpen" class="sm:hidden bg-dark-nav border-t border-white/10 p-2">
    <router-link
      to="/pricing"
      @click="menuOpen = false"
      class="block px-3 py-2 text-white font-medium rounded-md transition-colors"
      :class="route.path === '/pricing' ? 'bg-white/15' : 'hover:bg-white/10 active:bg-white/20'"
    >Pricing</router-link>
    <router-link
      v-if="authStore.isAuthenticated"
      to="/library"
      @click="menuOpen = false"
      class="flex items-center gap-2 px-3 py-2 text-white font-medium rounded-md transition-colors"
      :class="isLibraryActive ? 'bg-white/15' : 'hover:bg-white/10 active:bg-white/20'"
    >
      <LayoutGrid :size="16" />
      Videos
    </router-link>
    <button
      v-if="authStore.isAuthenticated"
      @click="openAccountSettings(); menuOpen = false"
      class="flex items-center gap-2 w-full text-left px-3 py-2 text-white font-medium rounded-md hover:bg-white/10 active:bg-white/20 transition-colors"
    >
      <Settings :size="16" />
      Account settings
    </button>
    <button
      v-if="authStore.isAuthenticated"
      @click="handleLogout(); menuOpen = false"
      class="block w-full text-left px-3 py-2 text-white font-medium rounded-md hover:bg-white/10 active:bg-white/20 transition-colors"
    >Log out</button>
  </div>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useAccountSettingsModal } from '@/composables/useAccountSettingsModal'
import { Menu, X, LayoutGrid, Settings, LogOut, Coins } from 'lucide-vue-next'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const { open: openAccountSettings } = useAccountSettingsModal()

const menuOpen = ref(false)
const userMenuOpen = ref(false)
const userMenuRef = ref<HTMLElement | null>(null)

const isLibraryActive = computed(() =>
  route.path.startsWith('/library') || route.path.startsWith('/tours')
)

watch(() => route.path, () => {
  menuOpen.value = false
  userMenuOpen.value = false
})

function handleOutsideClick(e: MouseEvent) {
  if (!userMenuOpen.value) return
  if (userMenuRef.value && !userMenuRef.value.contains(e.target as Node)) {
    userMenuOpen.value = false
  }
}
onMounted(() => document.addEventListener('click', handleOutsideClick))
onBeforeUnmount(() => document.removeEventListener('click', handleOutsideClick))

const avatarLetter = computed(() => {
  const first = authStore.user?.first_name?.trim()
  if (first) return first[0].toUpperCase()
  const email = authStore.user?.email
  return email ? email[0].toUpperCase() : '?'
})

function handleLogout() {
  authStore.logout()
  router.push('/login')
}
</script>
